import { Component, ChangeDetectionStrategy, OnInit, inject, signal, computed } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { AdminService } from '@generated/services/admin.service';
import { LiveKitSipTrunk } from '@generated/models/live-kit-sip-trunk';
import { LiveKitSipDispatchRule } from '@generated/models/live-kit-sip-dispatch-rule';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';

type DeleteKind = 'trunk' | 'dispatchRule';

@Component({
  selector: 'app-admin-livekit',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    TableModule,
    DrawerModule,
    TabsModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ProgressSpinnerModule,
    DeleteConfirmationComponent
],
  templateUrl: './admin-livekit.html',
  styleUrl: './admin-livekit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminLivekitComponent implements OnInit {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);

  // Data
  readonly inboundTrunks = signal<LiveKitSipTrunk[]>([]);
  readonly outboundTrunks = signal<LiveKitSipTrunk[]>([]);
  readonly dispatchRules = signal<LiveKitSipDispatchRule[]>([]);

  // Client-side search by trunk_id
  readonly trunkSearchQuery = signal<string>('');
  readonly filteredInboundTrunks = computed(() => this.filterTrunks(this.inboundTrunks()));
  readonly filteredOutboundTrunks = computed(() => this.filterTrunks(this.outboundTrunks()));

  private filterTrunks(list: LiveKitSipTrunk[]): LiveKitSipTrunk[] {
    const q = this.trunkSearchQuery().trim().toLowerCase();
    if (!q) return list;
    return list.filter(t => (t.trunk_id ?? '').toLowerCase().includes(q));
  }

  readonly isLoadingTrunks = signal<boolean>(false);
  readonly isLoadingRules = signal<boolean>(false);
  readonly isProvisioningTrunks = signal<boolean>(false);

  // Detail drawers
  readonly selectedTrunk = signal<LiveKitSipTrunk | null>(null);
  readonly trunkDrawerVisible = signal<boolean>(false);

  readonly selectedRule = signal<LiveKitSipDispatchRule | null>(null);
  readonly ruleDrawerVisible = signal<boolean>(false);

  // Delete confirmation (shared for trunk + dispatch rule)
  readonly deleteKind = signal<DeleteKind | null>(null);
  readonly deleteId = signal<string>('');
  readonly deleteName = signal<string>('');
  readonly deleteDialogVisible = signal<boolean>(false);
  readonly isDeleting = signal<boolean>(false);

  ngOnInit(): void {
    this.loadTrunks();
    this.loadRules();
  }

  refresh(): void {
    this.loadTrunks();
    this.loadRules();
  }

  /** POST /api/Admin/livekit/sip/system-trunks — upsert built-in
   *  системных SIP-trunk'ов и их dispatch rules на кластере LiveKit.
   *  Idempotent: если trunk уже существует, эндпоинт его пропускает.
   *  Recreate=false — не переcоздаём. После апдейта дергаем refresh,
   *  чтобы новые trunk'и/правила появились в таблице. */
  askProvisionSystemTrunks(): void {
    if (this.isProvisioningTrunks()) return;
    this.isProvisioningTrunks.set(true);
    this.adminService.apiAdminLivekitSipSystemTrunksPost({ recreate: false }).subscribe({
      next: (result) => {
        this.isProvisioningTrunks.set(false);
        const count = result.trunks?.length ?? 0;
        this.notificationService.showSuccess(
          $localize`:@@adminLivekit.notify.systemTrunksProvisioned:System trunks provisioned (${count}:count: trunk(s))`
        );
        this.loadTrunks();
        this.loadRules();
      },
      error: (error) => {
        this.isProvisioningTrunks.set(false);
        console.error('Failed to provision system trunks:', error);
        this.notificationService.showError(
          $localize`:@@adminLivekit.notify.provisionFail:Failed to provision system trunks`
        );
      }
    });
  }

  onTrunkSearchInput(value: string): void {
    this.trunkSearchQuery.set(value);
  }


  copyText(text: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.notificationService.showSuccess(
        $localize`:@@common.copied:Copied`
      );
    });
  }

  private loadTrunks(): void {
    this.isLoadingTrunks.set(true);
    this.adminService.apiAdminLivekitSipTrunksGet().subscribe({
      next: (result) => {
        this.inboundTrunks.set(result.inbound ?? []);
        this.outboundTrunks.set(result.outbound ?? []);
        this.isLoadingTrunks.set(false);
      },
      error: (error) => {
        console.error('Failed to load LiveKit trunks:', error);
        this.notificationService.showError(
          $localize`:@@adminLivekit.notify.loadTrunksFail:Failed to load trunks`
        );
        this.isLoadingTrunks.set(false);
      }
    });
  }

  private loadRules(): void {
    this.isLoadingRules.set(true);
    this.adminService.apiAdminLivekitSipDispatchRulesGet().subscribe({
      next: (rules) => {
        this.dispatchRules.set(rules ?? []);
        this.isLoadingRules.set(false);
      },
      error: (error) => {
        console.error('Failed to load LiveKit dispatch rules:', error);
        this.notificationService.showError(
          $localize`:@@adminLivekit.notify.loadRulesFail:Failed to load dispatch rules`
        );
        this.isLoadingRules.set(false);
      }
    });
  }

  // -- Detail drawers --

  openTrunk(trunk: LiveKitSipTrunk): void {
    this.selectedTrunk.set(trunk);
    this.trunkDrawerVisible.set(true);
  }
  closeTrunkDrawer(): void {
    this.trunkDrawerVisible.set(false);
    this.selectedTrunk.set(null);
  }

  openRule(rule: LiveKitSipDispatchRule): void {
    this.selectedRule.set(rule);
    this.ruleDrawerVisible.set(true);
  }
  closeRuleDrawer(): void {
    this.ruleDrawerVisible.set(false);
    this.selectedRule.set(null);
  }

  // -- Delete flow --

  askDeleteTrunk(trunk: LiveKitSipTrunk): void {
    if (!trunk.trunk_id) return;
    this.deleteKind.set('trunk');
    this.deleteId.set(trunk.trunk_id);
    this.deleteName.set(trunk.name || trunk.trunk_id);
    this.deleteDialogVisible.set(true);
  }

  askDeleteRule(rule: LiveKitSipDispatchRule): void {
    if (!rule.dispatch_rule_id) return;
    this.deleteKind.set('dispatchRule');
    this.deleteId.set(rule.dispatch_rule_id);
    this.deleteName.set(rule.name || rule.dispatch_rule_id);
    this.deleteDialogVisible.set(true);
  }

  cancelDelete(): void {
    this.deleteDialogVisible.set(false);
    this.deleteKind.set(null);
    this.deleteId.set('');
    this.deleteName.set('');
  }

  confirmDelete(): void {
    const kind = this.deleteKind();
    const id = this.deleteId();
    if (!kind || !id || this.isDeleting()) return;

    this.isDeleting.set(true);
    const req$ = kind === 'trunk'
      ? this.adminService.apiAdminLivekitSipTrunksTrunkIdDelete({ trunkId: id })
      : this.adminService.apiAdminLivekitSipDispatchRulesDispatchRuleIdDelete({ dispatchRuleId: id });

    req$.subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.deleteDialogVisible.set(false);
        this.notificationService.showSuccess(
          kind === 'trunk'
            ? $localize`:@@adminLivekit.notify.trunkDeleted:Trunk deleted`
            : $localize`:@@adminLivekit.notify.ruleDeleted:Dispatch rule deleted`
        );
        // Refresh just the affected list
        if (kind === 'trunk') {
          this.loadTrunks();
        } else {
          this.loadRules();
        }
        this.deleteKind.set(null);
        this.deleteId.set('');
        this.deleteName.set('');
      },
      error: (error) => {
        this.isDeleting.set(false);
        console.error('Delete failed:', error);
        this.notificationService.showError(
          $localize`:@@adminLivekit.notify.deleteFail:Delete failed`
        );
      }
    });
  }

  // Labels
  get deleteTitle(): string {
    return this.deleteKind() === 'trunk'
      ? $localize`:@@adminLivekit.delete.trunkTitle:Delete trunk`
      : $localize`:@@adminLivekit.delete.ruleTitle:Delete dispatch rule`;
  }

  get deleteSubtitle(): string {
    const name = this.deleteName();
    return this.deleteKind() === 'trunk'
      ? $localize`:@@adminLivekit.delete.trunkSubtitle:Delete trunk '${name}:name:' from LiveKit? This cannot be undone.`
      : $localize`:@@adminLivekit.delete.ruleSubtitle:Delete dispatch rule '${name}:name:' from LiveKit? This cannot be undone.`;
  }
}
